import React, { useContext } from 'react';
import { VenueTemplate } from '@go-shared/types/venueTemplate';
import { BareContentTemplateLegacy } from '@go-shared/types/contentTemplate';
import { FeedEditorHotspot } from '@go-client/components';
import { FeedArea } from '@go-shared/types/feedTemplate';
import { Plan } from '@go-shared/types/plan';
import { DEFAULT_TIMESTR_FORMAT, timeToMinutes, timeToString } from '@go-shared/lib/time';
import { FeedEditorCtx } from '@go-client/context/FeedEdtitorContext';
import { getVenueAvailabilityStrings, getVenueLocation } from '@go-shared/lib/venue';
import { Language } from '@go-shared/types/info';
import { getHourFormat } from '@go-shared/lib/property';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import { BrandCommonTextValues } from '@go-shared/types/brand';
import { getCommonTexts } from '@go-shared/lib/brand';

interface VenuesRundownHotspotProps {
  contentTemplate: BareContentTemplateLegacy;
  area: FeedArea;
  selectedLanguage: Language | undefined;
  showMergedVars: boolean;
  highlightAreas: boolean;
  onDrop?: (contentTemplate: BareContentTemplateLegacy) => void;
  onAreaDeleted: (area: FeedArea) => void;
  editorLocked: boolean;
  commonTexts: BrandCommonTextValues;
  mcmKey: string;
}

export const VenuesRundownHotspot: React.FC<VenuesRundownHotspotProps> = ({
  contentTemplate,
  area,
  onDrop,
  onAreaDeleted,
  selectedLanguage,
  showMergedVars,
  highlightAreas,
  editorLocked,
  mcmKey,
}) => {
  const { plan, venueTemplates, settingsPanel } = useContext(FeedEditorCtx);

  const contentTemplateReady: BareContentTemplateLegacy = {
    ...contentTemplate,
    content: buildContentForVenueRundown(
      area.content!.params!.venueRundownConfig!,
      venueTemplates,
      plan!,
      selectedLanguage
    ),
  };
  if (!area.style) area.style = {};
  const isSelected =
    settingsPanel?.type === 'venue-rundown-settings' &&
    area.content!._id === settingsPanel.venueRundownId;
  (area.style as any).width = '100%';
  (area.style as any).height = area.content!.params!.venueRundownConfig!.length ? 'auto' : 100;
  area.className = 'relative';

  return (
    <div
      className={`venue-rundown relative w-full ${
        isSelected ? 'outline-venue-rundown' : ''
      } rounded`}
    >
      <FeedEditorHotspot
        onAreaDeleted={onAreaDeleted}
        highlightAreas={highlightAreas}
        onElementCreated={() => null}
        onElementChanged={() => null}
        onColumnCreated={() => null}
        onRowMove={() => null}
        onRowCreated={() => null}
        onContentChange={() => null}
        contentTemplate={contentTemplateReady}
        key={area.content!._id}
        area={area}
        onDrop={onDrop}
        selectedLanguage={selectedLanguage}
        showMergedVars={showMergedVars}
        editorLocked={editorLocked}
        mcmKey={mcmKey}
      />
    </div>
  );
};

const buildContentForVenueRundown = (
  ids: string[],
  vts: VenueTemplate[],
  plan: Plan,
  selectedLanguage?: Language
) => {
  const { commonTextTranslations } = useContext(FeedEditorCtx);
  const { brand } = useServerInfo();
  const hourFormat = getHourFormat(plan.property);

  const commonTexts = getCommonTexts(
    {
      default: brand.commonTexts,
      translations: commonTextTranslations,
    },
    { selected: selectedLanguage?.key }
  );

  let content = '';
  ids &&
    ids.forEach(id => {
      const template = vts.find(vt => vt._id === id);

      if (template) {
        content += `<div class="venue-rundown-row table-row w-full">`;
        const venueData = plan.venueData.find(vd => vd.template === id);
        const dayStartMinutes = timeToMinutes(plan.dayStart);
        content += '<div class="venue-rundown-times table-cell whitespace-no-wrap">';
        if (venueData) {
          let availability = getVenueAvailabilityStrings(
            venueData.hours,
            dayStartMinutes,
            hourFormat,
            DEFAULT_TIMESTR_FORMAT,
            commonTexts,
            venueData?.displayOptions?.timeDisplay
          );
          content += availability.map(hour => `<p class="mr-5">${hour}</p>`).join('\n');
        } else {
          content += '<p class="mr-5">';
          content += `${timeToString(
            plan.dayStart,
            hourFormat,
            DEFAULT_TIMESTR_FORMAT
          )} - ${timeToString(plan.dayStart, hourFormat, DEFAULT_TIMESTR_FORMAT)}`;
          content += '</p>';
        }
        content += '</div>';

        let venueDisplayName = venueData?.displayOptions?.displayName || template.name;
        let venueLevelName = venueData?.displayOptions?.deckDisplayName || String(template.level);
        if (venueLevelName.trim() !== '') {
          venueLevelName += ` ${getVenueLocation(template, commonTexts)}`;

          const deckLabel = `<span class="venue-rundown-deck-separator">, </span><span class="venue-rundown-deck-name">${commonTexts.deck}</span> ${venueLevelName}`;
          // Add the venue next to the name, but hide by default
          venueDisplayName += `<span class="venue-rundown-inline-deck" style="display: none">${deckLabel}</span>`;
        }

        content += `<div class="venue-rundown-venue-name table-cell"><p>${venueDisplayName}</p></div>`;

        content += `<div class="venue-rundown-deck table-cell"><p>${venueLevelName}</p></div>`;

        content += '</div>';
      }
    });
  return `<div>${content}</div`;
};
